import { create } from 'zustand';
import type { ProductDesignerConfig, VDPField, NameNumber, PrintingMethod, LayoutFeatureConfig, GlbDesignPlacement, ProductVariantDTO, ProductColorVariantDTO, PrintColorOption } from '@customedge/shared';
import { DEFAULT_LAYOUT_FEATURE_CONFIG, printColorOptionToString } from '@customedge/shared';
import { useHistoryStore, restoreCanvasJsonSilently, HISTORY_SNAPSHOT_PROPS } from './historyStore';

// Fabric types — loaded dynamically, use any to avoid static dependency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FabricCanvas = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FabricObject = any;

export interface DesignSide {
  sideKey:      string;
  label:        string;
  // Base label of the underlying photo (e.g. "Voor"), without the placeholder
  // suffix — used to group sibling placeholders under one tab in SideSelector.
  photoLabel:   string;
  // Label of just this placeholder area within the photo (e.g. "Logo"), when
  // the photo has more than one placeholder. Undefined for single-placeholder photos.
  placeholderLabel?: string;
  // ID of the underlying ImageSideConfig (photo) this area belongs to — several
  // `DesignSide` entries can share the same `photoId` when one photo has more
  // than one placeholder area; used to show every sibling area's guide box
  // together on that photo instead of just the currently active one.
  photoId:      string;
  imagePath:    string;
  canvasWidth:  number;
  canvasHeight: number;
  areaLeft:     number;
  areaTop:      number;
  designAreas:  any[];
  /** Where this placeholder's design lands on the product's 3D model, if the
   * admin has positioned it there — undefined means it isn't textured onto
   * the 3D model at all. */
  glbBox?: GlbDesignPlacement;
  // Per-side override of the product-wide printColorOptions (raw serialized
  // string, e.g. "full-color,1,2,3" — parse with parsePrintColorOptions
  // before use). Undefined = no override, falls back to productConfig's own
  // printColorOptions. See ImageSide.printColorOptions for the full rationale.
  printColorOptions?: string;
  // Which of the product's printPositions chips this side belongs to, if any
  // (undefined = position-agnostic, same convention as ImageSide.printPosition
  // in shared/src/types/design.ts). Carried through from useProductSetup.ts's
  // own `side.printPosition` (used there to filter/resolve sides, but
  // otherwise dropped before this point) so consumers of the store's `sides`
  // — e.g. BackgroundColorTool.tsx resolving the active side's own per-position
  // print-color pick — can look up selectedPrintColorByPosition /
  // selectedPrintMethodByPosition for the CURRENTLY ACTIVE side without
  // re-deriving position filtering themselves.
  printPosition?: string;
  // Per-side override of the product-wide printAreaSizeOptions (raw
  // serialized string, e.g. "100,300" -- parse with
  // parsePrintAreaSizeOptions before use). A SEPARATE dimension from
  // printColorOptions above, for methods whose real selectable tiers are
  // physical print size (cm2), not color count -- see
  // ImageSide.printAreaSizeOptions for the full rationale. Undefined = no
  // area-size tiers for this side.
  printAreaSizeOptions?: string;
}

interface DesignStore {
  sides:           DesignSide[];
  activeSideKey:   string;
  canvasInstances: Map<string, FabricCanvas>;
  // Only the *active* side's Fabric canvas is ever mounted (DesignerWorkspace
  // renders one <canvas> reused across sides) — switching sides disposes the
  // old canvas entirely. Without this cache, that side's edits vanish the
  // instant you switch away, both for "load it back on revisit" and for
  // "still include it when saving/adding to cart" (the canvas instance is
  // simply gone from `canvasInstances` by then). Keyed by sideKey, holds the
  // last `toJSON()` snapshot taken right before disposal.
  //
  // LEGACY as of the per-print-color-count feature below — still written/read
  // as a fallback for designs saved before that feature existed (see
  // loadFromTemplate's back-compat shim), but no longer the primary cache for
  // any design touched under the new code. Do not add new call sites that
  // read/write this directly; use sideColorCountJsonCache instead.
  sideJsonCache:   Record<string, any>;
  // Per-side, per-print-color-count snapshot of that side's canvas content
  // (texts, images, background) — sideJsonCache's old flat shape (one
  // snapshot per side, full stop) couldn't tell two different Print Color
  // counts' content apart, so switching from e.g. "2 color" to "1 color" and
  // back used to silently share (and corrupt) one snapshot across every count
  // the customer tried. Outer key is sideKey (same as sideJsonCache); inner
  // key is printColorOptionToString(the count active for that side) — see
  // resolveActiveColorCountKey below for how "the count active for that side"
  // is derived (flat selectedPrintColorOption vs. per-position
  // selectedPrintColorByPosition). A side/count combination with no entry
  // here yet means that count has never been visited on that side — it
  // mounts blank, it does NOT inherit another count's content ("restrict &
  // remember", not "share").
  sideColorCountJsonCache: Record<string /* sideKey */, Record<string /* ColorCountKey */, any>>;
  // True while useDesignWip.ts/useSharedDesignLoader.ts are mid-sequence
  // restoring a saved design (?dwip=/?design=): their Phase 1 sets
  // selectedPrintColorOption/selectedPrintColorByPosition BEFORE Phase 2 calls
  // loadFromTemplate with the real saved canvas content. Without this flag,
  // useFabricCanvas.ts's color-count-switch effect reacts to Phase 1's
  // selection change in between the two phases, snapshotting the
  // not-yet-loaded (blank) canvas and pushing a spurious history entry —
  // harmless to the final on-screen result (Phase 2 overwrites it a moment
  // later) but pollutes undo history with a blank state the customer never
  // created. Both loader hooks set this true when they start Phase 1's
  // restore and false once Phase 2's loadFromTemplate call returns.
  isRestoringSavedDesign: boolean;
  // Natural pixel size of each product photo, learned async on image load.
  // The design-area rectangle is often much smaller than the photo itself, so
  // "fit to viewport" must size against this, not just the canvas/area dimensions.
  // Keyed by `imagePath` (not sideKey) — the same photo URL is often shared by
  // several sides/areas (e.g. one print method's placeholder box next to
  // another's on the identical product shot), and the browser only fires an
  // `<img>`'s `onLoad` once per distinct `src`. A sideKey-keyed cache would
  // never gain an entry for a side whose photo was already loaded under a
  // different side's key, leaving its "fit to viewport" permanently unable
  // to find a natural size for an image it can already see on screen.
  imageNaturalSizes: Map<string, { w: number; h: number }>;
  selectedObject:  FabricObject | null;
  productConfig:   ProductDesignerConfig | null;
  isDisplay:       '0' | '1';
  savedDesignId:   string | null;
  // REAL BUG found+fixed 2026-08-21 (live user report: editing text and
  // saving again after the Preview tab had already reached DONE once left
  // the tab permanently stuck showing the OLD render): PreviewView.tsx's
  // embroidery-status poll stops once it observes DONE (by design — no
  // point polling a status that isn't changing), but nothing told it a
  // BRAND NEW save had happened and a fresh render was now in flight, so
  // the query's cached DONE state made `refetchInterval` return `false`
  // forever, even after a genuinely new render started server-side.
  // Bumped on every successful save (both the manual Save button's
  // saveCurrentDesign() in DesignerHeader.tsx and useAutoSave.ts's own
  // POST) — PreviewView.tsx includes this in its query key, so a changed
  // value forces a fresh fetch/re-evaluation of the polling condition
  // instead of trusting stale cached data indefinitely.
  savedDesignVersion: number;
  // REAL BUG found+fixed 2026-08-27 (live user report: editing a position's
  // text/image and immediately switching to the Preview tab — via "Volgende"
  // or the tab itself — kept showing that side's STALE embossing/embroidery
  // relief render with no indication anything was wrong, since nothing
  // server-side had changed yet: the only save trigger was the 30s
  // useAutoSave.ts timer, and PreviewView.tsx's loading badge only reacts to
  // genuinely QUEUED/PROCESSING server status). Tracks which sides have real
  // canvas edits since the last successful save, entirely client-side, so
  // PreviewView.tsx can hide a stale cached render and show a loading badge
  // IMMEDIATELY on tab switch, before any server round-trip is even
  // possible. Marked via useFabricCanvas.ts's own object:added/removed/
  // modified handlers (same __suppressHistory guard the history-snapshot
  // handler already uses, so undo/redo/programmatic restores don't
  // spuriously mark dirty); cleared on every successful save (useAutoSave.ts,
  // DesignerHeader.tsx's saveCurrentDesign, useFinishDesign.ts) since all
  // three paths save every side's full current content, not a partial one.
  dirtySideKeys:   Set<string>;
  // REAL BUG found+fixed 2026-08-28 (live user report: add image 1, remove
  // it, add image 2, immediately navigate to Preview tab then back to
  // Design — the OLD image 1 reappeared, not image 2). Root cause: placing
  // an image/QR-code/background is genuinely ASYNC (fabric.Image.fromURL
  // decodes before its callback fires and actually mutates the canvas —
  // see BackgroundColorTool.tsx's commitToCanvas, QRCodeTool.tsx's
  // addToCanvas, BackgroundTool.tsx's background-apply effect), but nothing
  // blocked a side/tab switch from happening first. useFabricCanvas.ts's
  // own cleanup (keyed on side change/unmount) synchronously snapshots the
  // canvas into sideColorCountJsonCache and disposes it — if that runs
  // BEFORE the pending image callback fires, the snapshot misses the new
  // image entirely, and the callback's later addImageFitted() call lands on
  // an already-disposed Fabric instance and silently does nothing. Tracks
  // how many such async commits are currently in flight (a count, not a
  // boolean, so two overlapping commits — e.g. a fast double-click — don't
  // let one's completion prematurely clear the guard for the other);
  // setActiveSide/DesignerApp.tsx's handleTabChange both poll this before
  // actually switching, so a switch attempted mid-commit waits (briefly,
  // normally well under 1s) instead of racing the disposal above.
  pendingCanvasCommits: number;
  artworkPrices:   Record<string, number>;
  templatePrice:   number;
  printingMethod:  PrintingMethod | null;
  nameNumbers:     NameNumber[];
  vdpData:         VDPField[];
  layoutConfig:    LayoutFeatureConfig;
  // Object Variants — one real GLB mesh mapped to a Variant, each with a
  // curated subset of Master Variant colors/textures. Selection is keyed by
  // variant id; the value is either a curated `ProductVariantOptionDTO.id` or
  // `custom:#hex` when the customer used the free color picker.
  variants:        ProductVariantDTO[];
  selectedVariantOptions: Record<string, string>;
  // Vendor Product Color Variants — distinct from `variants` (the 3D-mesh
  // Object Variants system) above. Each owns its own full imageSides +
  // placeholder set; selecting one re-derives `sides` in useProductSetup.ts.
  // `activeColorVariantId` is null for a product with none configured, or
  // before one has been picked yet.
  colorVariants:        ProductColorVariantDTO[];
  activeColorVariantId: string | null;
  // True only once the customer has actually applied a choice in
  // ProductOptionsModal — useProductSetup.ts also calls setActiveColorVariant
  // to auto-pick the first variant so the canvas never opens on an empty
  // photo set, but that auto-pick must NOT read as "selected" to the
  // DesignerHeader's Variant pill (which should still show its "Select
  // Variant" placeholder until the customer actually chooses one).
  hasSelectedColorVariant: boolean;
  // Popup-selected print method / print color, shown in the header and sent
  // with the saved design — see ProductOptionsModal.tsx.
  selectedPrintMethod:      string | null;
  selectedPrintColorOption: PrintColorOption | null;
  // Popup-selected print POSITIONS — the grouping dimension ABOVE
  // selectedPrintMethod. Unlike print method (single value, one active
  // choice), the customer can select MULTIPLE positions simultaneously
  // (e.g. "voorzijde" AND "achterzijde"), each with its OWN independently-
  // chosen print method — see selectedPrintMethodByPosition below and
  // ProductOptionsModal.tsx. Empty array = position-agnostic (no filtering,
  // same "absent means show everything" default as selectedPrintMethod's
  // own null). Deliberately an array, not a single nullable string — do not
  // collapse this back to selectedPrintMethod's shape.
  selectedPrintPositions:   string[];
  // Per-position print method choice, keyed by position name (e.g.
  // { voorzijde: "Zeefdruk", achterzijde: "DTF Transfer" }) — only
  // meaningful when selectedPrintPositions is non-empty. Falls back to
  // selectedPrintMethod for a position with no entry here, so a product
  // that only ever sets selectedPrintMethod (no positions configured at
  // all) keeps behaving exactly as before this field existed.
  selectedPrintMethodByPosition: Record<string, string>;
  // Per-position print COLOR choice, same keying/fallback convention as
  // selectedPrintMethodByPosition above — added so a customer picking two
  // positions with different methods (e.g. Zeefdruk vs DTF Transfer) can
  // also pick each position's own color/tier independently, not just share
  // one narrowed-by-first-position color set. "For all" in
  // ProductOptionsModal.tsx is a UI convenience only — it writes the SAME
  // method+color into every selected position's entry here, there is no
  // separate "for all" mode tracked in the store itself.
  selectedPrintColorByPosition: Record<string, PrintColorOption>;
  // Per-position print AREA-SIZE choice (cm2), for methods with real
  // physical-size tiers instead of a color-count concept -- e.g. MidOcean's
  // Digitale transfer (TDT/TD1), PFConcept's DTF Transfer. A SEPARATE
  // dimension from selectedPrintColorByPosition above, never both
  // meaningful for the same position at once (a method with area-size
  // tiers has printColorOptions=[FULL_COLOR] only -- see
  // ImageSideConfig.printAreaSizeOptions's schema comment). Same
  // "For all" UI convenience / no separate store-level mode convention as
  // selectedPrintColorByPosition.
  selectedPrintAreaSizeByPosition: Record<string, number>;
  showGrid:        boolean;
  showRulers:      boolean;

  setSides:          (sides: DesignSide[]) => void;
  setActiveSide:     (key: string) => void;
  registerCanvas:    (key: string, canvas: FabricCanvas) => void;
  unregisterCanvas:  (key: string) => void;
  cacheSideJson:     (key: string, json: any) => void;
  // Analogous to cacheSideJson but keyed by (sideKey, colorCountKey) — see
  // sideColorCountJsonCache's field comment.
  cacheSideColorCountJson: (sideKey: string, colorCountKey: string, json: any) => void;
  // Resolves which color-count key a given side is "currently under": the
  // per-position pick (selectedPrintColorByPosition[side.printPosition]) when
  // the side belongs to a position AND that position has its own entry,
  // otherwise the flat selectedPrintColorOption — same precedence
  // BackgroundColorTool.tsx's defaultColorOption already established for the
  // identical question. Returns the sentinel '__unset__' when neither is set
  // yet (e.g. before the customer has ever opened the Print Color popup, or a
  // product with no printColorOptions configured at all) so callers always
  // get a deterministic string key instead of needing their own null-check.
  resolveActiveColorCountKey: (side: DesignSide) => string;
  setIsRestoringSavedDesign: (restoring: boolean) => void;
  setImageNaturalSize: (imagePath: string, w: number, h: number) => void;
  setSelectedObject: (obj: FabricObject | null) => void;
  setProductConfig:  (config: ProductDesignerConfig) => void;
  setSavedDesignId:  (id: string) => void;
  bumpSavedDesignVersion: () => void;
  markSideDirty:     (sideKey: string) => void;
  clearDirtySideKeys: () => void;
  beginCanvasCommit: () => void;
  endCanvasCommit:   () => void;
  addArtworkPrice:   (id: string, price: number) => void;
  setTemplatePrice:  (price: number) => void;
  setPrintingMethod: (method: PrintingMethod) => void;
  setNameNumbers:    (data: NameNumber[]) => void;
  setVdpData:        (data: VDPField[]) => void;
  loadFromTemplate:  (jsonMap: Record<string, any>) => void;
  getCustomizePrice: () => string;
  setLayoutConfig:   (config: LayoutFeatureConfig) => void;
  setVariants:       (variants: ProductVariantDTO[]) => void;
  setVariantOption:  (variantId: string, optionIdOrCustomHex: string) => void;
  // Bulk restore — used when reopening a saved design (My Designs, ?design=
  // share links, approval preview) so the 2D/3D previews show the variant the
  // customer actually picked, not the model's default appearance.
  setSelectedVariantOptions: (selections: Record<string, string>) => void;
  setColorVariants:      (colorVariants: ProductColorVariantDTO[]) => void;
  // Clears sideJsonCache/canvasInstances on every switch -- a color variant's
  // sides have entirely different ids than another's, so nothing in the old
  // cache is reachable/valid once `sides` is swapped; this just prevents
  // unbounded growth across repeated switches, see useProductSetup.ts.
  // `explicit` defaults true (a real user/API-driven pick) — useProductSetup.ts's
  // auto-select-first-variant effect is the one call site that passes `false`.
  setActiveColorVariant: (id: string | null, explicit?: boolean) => void;
  setSelectedPrintMethod:     (method: string | null) => void;
  setSelectedPrintColorOption: (option: PrintColorOption | null) => void;
  setSelectedPrintPositions:  (positions: string[]) => void;
  setSelectedPrintMethodByPosition: (methodByPosition: Record<string, string>) => void;
  setSelectedPrintColorByPosition: (colorByPosition: Record<string, PrintColorOption>) => void;
  setSelectedPrintAreaSizeByPosition: (areaSizeByPosition: Record<string, number>) => void;
  toggleGrid:        () => void;
  toggleRulers:      () => void;
}

export const useDesignStore = create<DesignStore>((set, get) => ({
  sides:           [],
  activeSideKey:   '',
  canvasInstances: new Map(),
  sideJsonCache:   {},
  sideColorCountJsonCache: {},
  isRestoringSavedDesign: false,
  imageNaturalSizes: new Map(),
  selectedObject:  null,
  productConfig:   null,
  isDisplay:       '0',
  savedDesignId:   null,
  savedDesignVersion: 0,
  dirtySideKeys:   new Set(),
  pendingCanvasCommits: 0,
  artworkPrices:   {},
  templatePrice:   0,
  printingMethod:  null,
  nameNumbers:     [],
  vdpData:         [],
  layoutConfig:    DEFAULT_LAYOUT_FEATURE_CONFIG,
  variants:        [],
  selectedVariantOptions: {},
  colorVariants:        [],
  activeColorVariantId: null,
  hasSelectedColorVariant: false,
  selectedPrintMethod:      null,
  selectedPrintColorOption: null,
  selectedPrintPositions:   [],
  selectedPrintMethodByPosition: {},
  selectedPrintColorByPosition: {},
  selectedPrintAreaSizeByPosition: {},
  showGrid:        false,
  showRulers:      false,

  setSides: (sides) => set({ sides, activeSideKey: sides[0]?.sideKey ?? '' }),

  // REAL BUG found+fixed 2026-08-28 — see pendingCanvasCommits's own field
  // comment for the full mechanism. Switching the active side while an
  // async image/QR/background commit is still in flight would let
  // useFabricCanvas.ts's own cleanup effect (keyed on this same
  // activeSideKey) snapshot-and-dispose the canvas BEFORE that commit's
  // callback ever mutates it — silently losing the just-placed content.
  // Polls rather than returning a Promise: every existing caller
  // (SideSelector.tsx) invokes this as a synchronous fire-and-forget
  // onClick, and a real await here would ripple into every call site for a
  // wait that's normally well under 1s. Guaranteed to terminate — every
  // beginCanvasCommit() call has a matching endCanvasCommit() in a finally
  // block, so this can never poll forever even if a commit's own image load
  // fails.
  setActiveSide: (key) => {
    const commit = () => set({ activeSideKey: key });
    if (get().pendingCanvasCommits > 0) {
      const poll = () => { if (get().pendingCanvasCommits > 0) setTimeout(poll, 50); else commit(); };
      poll();
    } else {
      commit();
    }
  },

  registerCanvas: (key, canvas) => set(state => {
    const m = new Map(state.canvasInstances);
    m.set(key, canvas);
    return { canvasInstances: m };
  }),

  unregisterCanvas: (key) => set(state => {
    const m = new Map(state.canvasInstances);
    m.delete(key);
    return { canvasInstances: m };
  }),

  cacheSideJson: (key, json) => set(state => ({
    sideJsonCache: { ...state.sideJsonCache, [key]: json },
  })),

  cacheSideColorCountJson: (sideKey, colorCountKey, json) => set(state => ({
    sideColorCountJsonCache: {
      ...state.sideColorCountJsonCache,
      [sideKey]: { ...state.sideColorCountJsonCache[sideKey], [colorCountKey]: json },
    },
  })),

  resolveActiveColorCountKey: (side) => {
    const { selectedPrintColorByPosition, selectedPrintColorOption } = get();
    const positionOption = side.printPosition ? selectedPrintColorByPosition[side.printPosition] : undefined;
    const effective = positionOption ?? selectedPrintColorOption;
    return effective != null ? printColorOptionToString(effective) : '__unset__';
  },
  setIsRestoringSavedDesign: (restoring) => set({ isRestoringSavedDesign: restoring }),

  setImageNaturalSize: (imagePath, w, h) => set(state => {
    const m = new Map(state.imageNaturalSizes);
    m.set(imagePath, { w, h });
    return { imageNaturalSizes: m };
  }),

  setSelectedObject: (obj) => set({ selectedObject: obj }),
  setProductConfig:  (config) => set({ productConfig: config }),
  setSavedDesignId:  (id) => set({ savedDesignId: id }),
  bumpSavedDesignVersion: () => set(state => ({ savedDesignVersion: state.savedDesignVersion + 1 })),
  markSideDirty: (sideKey) => set(state => {
    if (state.dirtySideKeys.has(sideKey)) return state; // no-op, avoid a pointless re-render on every keystroke
    const next = new Set(state.dirtySideKeys);
    next.add(sideKey);
    return { dirtySideKeys: next };
  }),
  clearDirtySideKeys: () => set({ dirtySideKeys: new Set() }),
  beginCanvasCommit: () => set(state => ({ pendingCanvasCommits: state.pendingCanvasCommits + 1 })),
  endCanvasCommit: () => set(state => ({ pendingCanvasCommits: Math.max(0, state.pendingCanvasCommits - 1) })),

  addArtworkPrice: (id, price) => set(state => ({
    artworkPrices: { ...state.artworkPrices, [id]: price },
  })),

  setTemplatePrice: (price) => set({ templatePrice: price }),
  setPrintingMethod: (method) => set({ printingMethod: method }),
  setNameNumbers: (data) => set({ nameNumbers: data }),
  setVdpData: (data) => set({ vdpData: data }),
  setLayoutConfig: (config) => set({ layoutConfig: config }),
  setVariants: (variants) => set({ variants }),
  setVariantOption: (variantId, optionIdOrCustomHex) => set(state => ({
    selectedVariantOptions: { ...state.selectedVariantOptions, [variantId]: optionIdOrCustomHex },
  })),
  setSelectedVariantOptions: (selections) => set({ selectedVariantOptions: selections }),
  setColorVariants: (colorVariants) => set({ colorVariants }),
  setActiveColorVariant: (id, explicit = true) => set(state => {
    // Only reset canvasInstances/sideJsonCache when the color variant is
    // ACTUALLY changing — clearing them is what forces DesignerWorkspace's
    // canvas to unmount/remount under the new variant's side keys, but if
    // `id` already equals the current activeColorVariantId (e.g.
    // useSharedDesignLoader restoring a saved design's colorVariantId that
    // matches what useProductSetup's own `?colorVariant=` URL param already
    // resolved), the underlying <canvas> component sees identical props and
    // never actually unmounts — so registerCanvas never re-fires and
    // canvasInstances stays permanently empty afterward. Confirmed live
    // 2026-08-03: reopening a saved design via the Promofit React PDP's
    // "Bewerken" link fetched the correct jsonObjects but the canvas stayed
    // blank forever because of exactly this no-op clear.
    if (state.activeColorVariantId === id) {
      return { hasSelectedColorVariant: explicit || state.hasSelectedColorVariant };
    }
    return {
      activeColorVariantId: id, sideJsonCache: {}, sideColorCountJsonCache: {}, canvasInstances: new Map(),
      hasSelectedColorVariant: explicit,
    };
  }),
  setSelectedPrintMethod: (method) => set({ selectedPrintMethod: method }),
  setSelectedPrintColorOption: (option) => set({ selectedPrintColorOption: option }),
  setSelectedPrintPositions: (positions) => set({ selectedPrintPositions: positions }),
  setSelectedPrintMethodByPosition: (methodByPosition) => set({ selectedPrintMethodByPosition: methodByPosition }),
  setSelectedPrintColorByPosition: (colorByPosition) => set({ selectedPrintColorByPosition: colorByPosition }),
  setSelectedPrintAreaSizeByPosition: (areaSizeByPosition) => set({ selectedPrintAreaSizeByPosition: areaSizeByPosition }),
  toggleGrid: () => set(state => ({ showGrid: !state.showGrid })),
  toggleRulers: () => set(state => ({ showRulers: !state.showRulers })),

  loadFromTemplate: (jsonMap) => {
    const { canvasInstances, sides, resolveActiveColorCountKey } = get();
    const sideByKey = new Map(sides.map(s => [s.sideKey, s]));

    // Each incoming entry is EITHER a legacy flat Fabric snapshot (has its own
    // `objects` array at the top level — a design saved before the
    // per-print-color-count feature existed, when there was only ever one
    // snapshot per side) OR the new nested shape (a plain map of
    // colorCountKey -> Fabric snapshot). Cheapest reliable discriminator:
    // does the entry itself have `objects`?
    const isLegacyFlatSnapshot = (entry: any): boolean =>
      entry && typeof entry === 'object' && 'objects' in entry;

    // Stamps every image node in a raw Fabric JSON tree with
    // `fromTemplate: true`, mutating in place — mirrors the backend's own
    // `collectRecolorTargets` tree-walk (templates.ts) for the same
    // `objects` + `backgroundImage` shape. MUST happen before this JSON is
    // written into sideColorCountJsonCache below, not only on the live
    // canvas objects after restore — useFabricCanvas.ts's own count-key
    // reconciliation effect can restore straight from that cache on a later
    // side/count switch, via restoreCanvasJsonSilently, completely
    // bypassing whatever tagging this function does to the LIVE canvas.
    // Confirmed live: tagging only the live objects worked on the very first
    // render after applying a template, but a subsequent count-key
    // reconciliation (firing moments later, before this fix) silently
    // restored the untagged cached JSON, wiping the tag and letting
    // ImageCorrectionModal.tsx trust a template's inherited (and possibly
    // foreign) originalUploadId again.
    const tagTemplateImages = (node: any): void => {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node.objects)) for (const child of node.objects) tagTemplateImages(child);
      if (node.backgroundImage) tagTemplateImages(node.backgroundImage);
      if (node.type === 'image') node.fromTemplate = true;
    };

    // Cache every side's JSON, not just the active one — only the active
    // side has a mounted canvas to apply to immediately; the rest would
    // otherwise be silently dropped and restored as blank when first visited.
    //
    // Per side, figure out which count key the live canvas (if mounted)
    // should actually be restored under, and normalize whatever shape this
    // entry arrived in into sideColorCountJsonCache's nested shape.
    const applyToCanvas: Array<{ canvas: FabricCanvas; json: any }> = [];

    set(state => {
      const nextCache = { ...state.sideColorCountJsonCache };
      const nextLegacyCache = { ...state.sideJsonCache };

      for (const [sideKey, entry] of Object.entries(jsonMap)) {
        const side = sideByKey.get(sideKey);
        const countKey = side ? resolveActiveColorCountKey(side) : '__unset__';

        if (isLegacyFlatSnapshot(entry)) {
          // Old design, single snapshot for this side, no color-count
          // association recorded anywhere — attribute it to whichever count
          // is ACTUALLY active for this side right now (Phase 1 of
          // useDesignWip.ts/useSharedDesignLoader.ts already restores
          // selectedPrintColorOption/selectedPrintColorByPosition BEFORE
          // calling loadFromTemplate, so this resolves correctly). This
          // means a pre-existing single-count design keeps working exactly
          // as before with zero visible change — a synthetic "legacy" key
          // would instead show a blank canvas the instant this feature
          // ships, since "legacy" would never match a real resolved count.
          tagTemplateImages(entry);
          nextCache[sideKey] = { ...nextCache[sideKey], [countKey]: entry };
          nextLegacyCache[sideKey] = entry;
          const canvas = canvasInstances.get(sideKey);
          if (canvas) applyToCanvas.push({ canvas, json: entry });
        } else {
          // Already the new nested shape (e.g. re-loading a WIP design saved
          // after this feature shipped) — merge directly.
          for (const snapshot of Object.values(entry)) tagTemplateImages(snapshot);
          nextCache[sideKey] = { ...nextCache[sideKey], ...entry };
          const canvas = canvasInstances.get(sideKey);
          const current = entry[countKey];
          // Explicitly blank the mounted canvas when this side's nested cache
          // has no snapshot for the CURRENTLY active count (e.g. the design
          // was saved with content under counts "1"/"2" but the customer has
          // count "3" active on reload) — without this, the canvas silently
          // keeps whatever was on it before this call instead of correctly
          // showing "nothing yet for this count" ("restrict & remember", not
          // "leave stale content visible").
          if (canvas) applyToCanvas.push({ canvas, json: current ?? { objects: [], background: '' } });
        }
      }

      return { sideColorCountJsonCache: nextCache, sideJsonCache: nextLegacyCache };
    });

    for (const { canvas, json } of applyToCanvas) {
      // `restoreCanvasJsonSilently` (not a plain `loadFromJSON`) — Fabric's
      // `clear()` step inside it fires a real 'object:removed' per existing
      // object, which the history-snapshot listener in useFabricCanvas.ts
      // would otherwise mistake for a real edit, corrupting undo/redo (see
      // historyStore.ts for full detail). Push one correct snapshot after
      // the restore so this still counts as a single undoable step, and
      // fire 'history:restored' so LayerPanel/the layer-count badge
      // pick up the newly-restored objects (none of them see 'object:added'
      // for what `loadFromJSON` restores).
      restoreCanvasJsonSilently(canvas, json).then(() => {
        // Tags every object this template restore just placed with
        // `fromTemplate: true` — a template's own jsonObjects were captured
        // from WHOEVER'S canvas originally created it (an admin's "Design on
        // canvas" flow — see admin Templates.tsx), so any `originalUploadId`/
        // `originalUploadUrl` baked into that JSON (see HISTORY_SNAPSHOT_PROPS)
        // belongs to that original session's customer/guest identity, not
        // the customer applying the template now. `originalUploadId` alone
        // being PRESENT is therefore not proof it's usable for THIS
        // customer — confirmed live: opening "Correct Colors" on a
        // template-placed image 404s on every backend call because
        // loadUploadedImage's ownership check (imageProcessing.ts) rejects
        // the mismatched customerId. ImageCorrectionModal.tsx reads this flag
        // to re-import the image fresh (importCatalogAssetAsUpload) instead
        // of trusting the inherited id — the same path already used when
        // originalUploadId is absent entirely. Survives save/reload via
        // HISTORY_SNAPSHOT_PROPS, since a customer can apply a template, save,
        // and come back later still needing this distinction.
        for (const obj of (canvas as any).getObjects?.() ?? []) {
          if (obj.type === 'image') obj.set({ fromTemplate: true });
        }
        useHistoryStore.getState().pushSnapshot(JSON.stringify((canvas as any).toJSON(HISTORY_SNAPSHOT_PROPS)));
        (canvas as any).fire('history:restored');
      });
    }
  },

  getCustomizePrice: () => {
    const { artworkPrices, templatePrice } = get();
    return JSON.stringify({
      artwork_price:  Object.values(artworkPrices).reduce((s, p) => s + p, 0),
      template_price: templatePrice,
    });
  },
}));
